import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  TrashIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  GlobeAltIcon,
  TagIcon,
  UserGroupIcon,
  SwatchIcon,
  PhotoIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import {
  STEP_ORDER,
  STEP_LABELS,
  STEP_HELPERS,
  inferWizardV2Progress,
  describeAudience,
  describeDesign,
  describeLogo,
  describeSeoKeywords,
  getSiteTypeLabel,
} from '../utils/wizardV2Progress';
import UtmAttributionCard from '../components/UtmAttributionCard';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const STEP_ICONS = {
  'initial-prompt': SparklesIcon,
  'website-type': GlobeAltIcon,
  'business-name': TagIcon,
  audience: UserGroupIcon,
  design: SwatchIcon,
  logo: PhotoIcon,
  seo: MagnifyingGlassIcon,
};

function formatDuration(fromIso, toIso) {
  if (!fromIso) return '—';
  const from = new Date(fromIso).getTime();
  const to = toIso ? new Date(toIso).getTime() : Date.now();
  const ms = Math.max(0, to - from);
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return '< 1m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours < 24) return `${hours}h ${mins}m`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return `${days}d ${remHours}h`;
}

const Pill = ({ children, className = '', tone = 'gray' }) => {
  const tones = {
    gray: 'bg-gray-100 text-gray-700',
    primary: 'bg-primary-100 text-primary-800',
    green: 'bg-green-100 text-green-800',
    red: 'bg-red-100 text-red-800',
    blue: 'bg-blue-100 text-blue-800',
    amber: 'bg-amber-100 text-amber-800',
    purple: 'bg-purple-100 text-purple-800',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full ${tones[tone] || tones.gray} ${className}`}
    >
      {children}
    </span>
  );
};

function CardHeader({ icon: Icon, title, subtitle, right }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        {Icon && (
          <div className="p-2 bg-primary-50 rounded-lg">
            <Icon className="w-5 h-5 text-primary-600" />
          </div>
        )}
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
  );
}

function StepStatusIcon({ state }) {
  if (state === 'done') {
    return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
  }
  if (state === 'current') {
    return (
      <span className="block w-5 h-5 rounded-full border-2 border-primary-500 bg-primary-500/20" />
    );
  }
  return <span className="block w-5 h-5 rounded-full border-2 border-gray-300" />;
}

function SiteBriefCard({ session }) {
  const ei = session.context?.extractedInfo || {};
  const audience = describeAudience(ei.targetAudience);
  const design = describeDesign(ei);
  const logo = describeLogo(ei);
  const seo = describeSeoKeywords(ei);
  const businessName = ei.companyName || ei.businessName;

  const Field = ({ label, children }) => (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
        {label}
      </div>
      <div className="text-sm text-gray-900">{children}</div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <CardHeader
        icon={DocumentDuplicateIcon}
        title="Site brief"
        subtitle="What the user assembled — same shape as the wizard's summary screen"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Site type">
          {ei.siteType ? (
            <div className="flex items-center gap-2">
              <Pill tone="blue">{getSiteTypeLabel(ei.siteType)}</Pill>
              {ei.ecommerceMode && (
                <span className="text-xs text-gray-500">
                  Mode: <span className="font-mono">{ei.ecommerceMode}</span>
                </span>
              )}
            </div>
          ) : (
            <span className="text-gray-400 italic">Not picked yet</span>
          )}
        </Field>

        <Field label="Business name">
          {businessName || <span className="text-gray-400 italic">Not picked yet</span>}
        </Field>

        <Field label="Audience">
          {audience && audience.count > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {(audience.labels || []).map((label, idx) => (
                <Pill key={`${label}-${idx}`} tone="purple">
                  {label}
                </Pill>
              ))}
              {audience.otherText && (
                <Pill tone="amber">
                  {audience.otherText}
                  <span className="ml-1 opacity-70">· custom</span>
                </Pill>
              )}
            </div>
          ) : (
            <span className="text-gray-400 italic">Not picked yet</span>
          )}
        </Field>

        <Field label="Language">
          <span className="font-mono text-sm">{(session.context?.language || 'en').toUpperCase()}</span>
          {session.context?.languageAnalysis?.confidence && (
            <span className="ml-2 text-xs text-gray-500">
              {Math.round(session.context.languageAnalysis.confidence * 100)}% confidence
            </span>
          )}
        </Field>

        <div className="md:col-span-2">
          <Field label="Selected design">
            {design && design.selectedImage ? (
              <div className="flex items-start gap-4">
                {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                <img
                  src={design.selectedImage}
                  alt="Selected design"
                  className="w-48 rounded border border-gray-200 shadow-sm"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <div className="text-xs text-gray-600">
                  {design.selectedTitle && (
                    <div className="font-medium text-gray-800 mb-1">{design.selectedTitle}</div>
                  )}
                  {ei.dribbbleColorPalette && (
                    <div className="flex items-center gap-1 mt-2">
                      {Object.values(ei.dribbbleColorPalette)
                        .filter((v) => typeof v === 'string' && /^#[0-9A-Fa-f]{6}$/.test(v))
                        .slice(0, 6)
                        .map((color, idx) => (
                          <div
                            key={`${color}-${idx}`}
                            className="w-6 h-6 rounded border border-gray-300"
                            style={{ backgroundColor: color }}
                            title={color}
                          />
                        ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <span className="text-gray-400 italic">Not picked yet</span>
            )}
          </Field>
        </div>

        <Field label="Logo">
          {logo && logo.url ? (
            <div className="flex items-center gap-3">
              {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
              <img
                src={logo.url}
                alt="Selected logo"
                className="w-20 h-20 rounded border border-gray-200 bg-gray-50 object-contain p-2"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="text-xs text-gray-600">
                <Pill tone={logo.source === 'upload' ? 'amber' : 'primary'}>
                  {logo.source === 'upload' ? 'Uploaded' : 'AI-generated'}
                </Pill>
                {logo.label && <div className="mt-1">{logo.label}</div>}
              </div>
            </div>
          ) : (
            <span className="text-gray-400 italic">Not picked yet</span>
          )}
        </Field>

        <Field label="SEO keywords">
          {seo.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {seo.map((k, idx) => (
                <Pill key={`${k}-${idx}`} tone="green">
                  {k}
                </Pill>
              ))}
            </div>
          ) : (
            <span className="text-gray-400 italic">Not picked yet</span>
          )}
        </Field>
      </div>
    </div>
  );
}

function StepTimelineCard({ session, progress }) {
  const ei = session.context?.extractedInfo || {};
  const audience = describeAudience(ei.targetAudience);
  const design = describeDesign(ei);
  const logo = describeLogo(ei);
  const seo = describeSeoKeywords(ei);
  const completed = new Set(progress.completedSteps || []);
  const currentStep = progress.currentStep;

  const stepRows = STEP_ORDER.map((step) => {
    let summary = null;
    switch (step) {
      case 'initial-prompt': {
        const prompt = session.context?.initialPrompt || ei.initialPrompt || '';
        if (prompt) {
          const excerpt = prompt.length > 200 ? `${prompt.slice(0, 197)}...` : prompt;
          summary = (
            <div className="text-xs text-gray-600 italic">"{excerpt}"</div>
          );
        }
        const langAnalysis = session.context?.languageAnalysis;
        if (langAnalysis?.detectedLanguage || session.context?.language) {
          summary = (
            <>
              {summary}
              <div className="text-[11px] text-gray-500 mt-1">
                Detected language: {(langAnalysis?.detectedLanguage || session.context?.language || 'en').toUpperCase()}
                {langAnalysis?.confidence != null &&
                  ` · ${Math.round(langAnalysis.confidence * 100)}% confidence`}
              </div>
            </>
          );
        }
        const refImages = ei.referenceImageUrls || [];
        if (Array.isArray(refImages) && refImages.length > 0) {
          summary = (
            <>
              {summary}
              <div className="text-[11px] text-gray-500 mt-1">
                {refImages.length} reference image{refImages.length === 1 ? '' : 's'} uploaded
              </div>
            </>
          );
        }
        break;
      }
      case 'website-type':
        if (ei.siteType) {
          const aiRecommended =
            ei.aiRecommendedSiteType && ei.aiRecommendedSiteType === ei.siteType;
          summary = (
            <div className="flex items-center gap-2">
              <Pill tone="blue">{getSiteTypeLabel(ei.siteType)}</Pill>
              {aiRecommended && <Pill tone="primary">AI recommended</Pill>}
              {ei.ecommerceMode && (
                <span className="text-xs text-gray-500">mode: {ei.ecommerceMode}</span>
              )}
            </div>
          );
        }
        break;
      case 'business-name':
        if (ei.companyName || ei.businessName) {
          summary = (
            <div className="text-sm text-gray-800 font-medium">
              {ei.companyName || ei.businessName}
            </div>
          );
        }
        break;
      case 'audience':
        if (audience && audience.count > 0) {
          const audienceLabels = audience.labels || (audience.label ? [audience.label] : []);
          summary = (
            <div className="text-xs text-gray-600">
              {audienceLabels.length} audience{audienceLabels.length === 1 ? '' : 's'}
              {audience.custom > 0 && ` + ${audience.custom} custom`}
            </div>
          );
        }
        break;
      case 'design':
        if (design && design.selectedImage) {
          summary = (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
              <img
                src={design.selectedImage}
                alt="Design thumbnail"
                className="w-16 h-10 rounded object-cover border border-gray-200"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <span>Design selected{design.selectedTitle ? `: ${design.selectedTitle}` : ''}</span>
            </div>
          );
        }
        break;
      case 'logo':
        if (logo && logo.url) {
          summary = (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
              <img
                src={logo.url}
                alt="Logo thumbnail"
                className="w-10 h-10 rounded border border-gray-200 bg-gray-50 object-contain p-1"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <span>{logo.source === 'upload' ? 'Uploaded' : 'AI-generated'} logo</span>
            </div>
          );
        }
        break;
      case 'seo':
        if (seo.length > 0) {
          summary = (
            <div className="text-xs text-gray-600">
              {seo.length} keyword{seo.length === 1 ? '' : 's'} selected
            </div>
          );
        }
        break;
      default:
        break;
    }

    let state = 'pending';
    if (completed.has(step)) state = 'done';
    else if (currentStep === step) state = 'current';

    return { step, state, summary };
  });

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <CardHeader
        icon={CheckCircleIcon}
        title="V2 step timeline"
        subtitle="Inferred from which extractedInfo keys are populated"
      />
      <ol className="space-y-4">
        {stepRows.map(({ step, state, summary }, idx) => {
          const Icon = STEP_ICONS[step];
          return (
            <li key={step} className="flex items-start gap-3">
              <div className="flex flex-col items-center">
                <StepStatusIcon state={state} />
                {idx < stepRows.length - 1 && (
                  <div
                    className={`w-px flex-1 mt-1 ${state === 'done' ? 'bg-green-300' : 'bg-gray-200'}`}
                    style={{ minHeight: '24px' }}
                  />
                )}
              </div>
              <div className="flex-1 pb-4">
                <div className="flex items-center gap-2">
                  {Icon && <Icon className="w-4 h-4 text-gray-400" />}
                  <span
                    className={`text-sm font-semibold ${state === 'pending' ? 'text-gray-400' : 'text-gray-900'}`}
                  >
                    {STEP_LABELS[step]}
                  </span>
                  {state === 'current' && <Pill tone="primary">In progress</Pill>}
                  {state === 'done' && <Pill tone="green">Done</Pill>}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{STEP_HELPERS[step]}</div>
                {summary && <div className="mt-2">{summary}</div>}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function PromptAnalysisCard({ session }) {
  const ctx = session.context || {};
  const ei = ctx.extractedInfo || {};
  const prompt = ctx.initialPrompt || ei.initialPrompt || '';
  const langAnalysis = ctx.languageAnalysis;
  const refImages = ei.referenceImageUrls || [];

  return (
    <details className="bg-white rounded-lg shadow p-6 group" open>
      <summary className="cursor-pointer list-none">
        <CardHeader
          icon={SparklesIcon}
          title="Initial prompt & analysis"
          subtitle="Raw prompt + language detection + reference images"
          right={
            <span className="text-xs text-gray-400 group-open:hidden">Click to expand</span>
          }
        />
      </summary>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
            Initial prompt
          </div>
          {prompt ? (
            <p className="text-sm text-gray-800 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap">
              {prompt}
            </p>
          ) : (
            <p className="text-sm text-gray-400 italic">No prompt captured</p>
          )}
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
            Language analysis
          </div>
          {langAnalysis ? (
            <pre className="text-xs bg-gray-50 rounded-lg p-3 overflow-x-auto max-h-48">
              {JSON.stringify(langAnalysis, null, 2)}
            </pre>
          ) : (
            <p className="text-sm text-gray-400 italic">No language analysis stored</p>
          )}
        </div>
        {refImages.length > 0 && (
          <div className="md:col-span-2">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
              Reference images ({refImages.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {refImages.slice(0, 6).map((url, idx) => (
                /* eslint-disable-next-line jsx-a11y/img-redundant-alt */
                <img
                  key={`${url}-${idx}`}
                  src={url}
                  alt={`Reference ${idx + 1}`}
                  className="w-32 h-32 object-cover rounded border border-gray-200"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </details>
  );
}

function GenerationOutcomeCard({ linkedWebsite }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <CardHeader icon={GlobeAltIcon} title="Generation outcome" />
      {linkedWebsite ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Pill
              tone={
                linkedWebsite.deployment_status === 'deployed'
                  ? 'green'
                  : linkedWebsite.deployment_status === 'failed'
                  ? 'red'
                  : 'blue'
              }
            >
              {linkedWebsite.deployment_status || linkedWebsite.status || 'unknown'}
            </Pill>
            <span className="text-sm text-gray-700 font-medium">
              {linkedWebsite.name || '(unnamed website)'}
            </span>
          </div>
          <div className="text-xs text-gray-500">
            <span className="font-mono">{linkedWebsite.id}</span>
          </div>
          {(linkedWebsite.custom_domain_url || linkedWebsite.render_url) && (
            <div className="text-sm">
              <a
                href={linkedWebsite.custom_domain_url || linkedWebsite.render_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-800 underline"
              >
                {linkedWebsite.custom_domain_url || linkedWebsite.render_url}
              </a>
            </div>
          )}
          {linkedWebsite.user_id && (
            <a
              href={`/accounts/${linkedWebsite.user_id}`}
              className="inline-block text-xs text-primary-600 hover:text-primary-800 mt-1"
            >
              View account →
            </a>
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-500 italic">
          No website has been generated for this session yet.
        </p>
      )}
    </div>
  );
}

const WizardSessionDetail = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [legacyError, setLegacyError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSessionDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const fetchSessionDetails = async () => {
    try {
      setLoading(true);
      setLegacyError(null);
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/wizard-sessions/${sessionId}`
      );
      setSession(response.data.data);
    } catch (error) {
      if (error.response?.status === 410 && error.response?.data?.error === 'legacy_v1_session') {
        setLegacyError(error.response.data);
        setSession(null);
      } else {
        console.error('Error fetching session details:', error);
        toast.error('Failed to fetch session details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this wizard session?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/wizard-sessions/${sessionId}`);
      toast.success('Session deleted successfully');
      navigate('/wizard-sessions');
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('Failed to delete session');
    }
  };

  const copySessionId = async () => {
    try {
      await navigator.clipboard.writeText(sessionId);
      toast.success('Session ID copied');
    } catch {
      toast.error('Failed to copy session ID');
    }
  };

  const progress = useMemo(
    () => (session ? session.progress || inferWizardV2Progress(session) : null),
    [session]
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="py-12">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  // Legacy v1 fallback
  if (legacyError) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/wizard-sessions')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Wizard Sessions
          </button>
          <button onClick={handleDelete} className="btn-danger flex items-center">
            <TrashIcon className="h-5 w-5 mr-2" />
            Delete Session
          </button>
        </div>
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
            <ClockIcon className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Legacy v1 wizard session</h2>
          <p className="text-sm text-gray-600 max-w-md mx-auto mb-6">
            {legacyError.message ||
              'The new admin detail view only supports v2 sessions. This session was created with the legacy conversational wizard and is shown here as a thin record only.'}
          </p>
          <div className="inline-block bg-gray-50 rounded-lg p-4 text-left text-xs text-gray-700 font-mono">
            <div>session_id: {legacyError.session?.session_id || sessionId}</div>
            {legacyError.session?.created_at && (
              <div>created_at: {legacyError.session.created_at}</div>
            )}
            {legacyError.session?.expires_at && (
              <div>expires_at: {legacyError.session.expires_at}</div>
            )}
            <div>completed: {String(legacyError.session?.completed)}</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!session) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-gray-500">Session not found</p>
        </div>
      </AdminLayout>
    );
  }

  const ei = session.context?.extractedInfo || {};
  const businessName = ei.companyName || ei.businessName || '(untitled session)';
  const expiresAt = session.expires_at ? new Date(session.expires_at) : null;
  const isExpired = !session.completed && expiresAt && expiresAt < new Date();
  const statusTone = session.completed ? 'green' : isExpired ? 'red' : 'blue';
  const statusLabel = session.completed ? 'Completed' : isExpired ? 'Expired' : 'Active';
  const timeSpent = formatDuration(session.created_at, session.completed_at || null);
  const language = (session.context?.language || ei.languages?.[0] || 'en').toUpperCase();
  const stepsCompleted = progress?.stepsCompleted ?? 0;
  const currentStepLabel = progress?.currentStep
    ? STEP_LABELS[progress.currentStep] || progress.currentStep
    : '—';

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Action bar */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/wizard-sessions')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Wizard Sessions
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchSessionDetails}
              className="btn-secondary flex items-center"
              disabled={loading}
            >
              <ArrowPathIcon className={`h-5 w-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button onClick={handleDelete} className="btn-danger flex items-center">
              <TrashIcon className="h-5 w-5 mr-2" />
              Delete Session
            </button>
          </div>
        </div>

        {/* Header card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold text-gray-900 truncate">{businessName}</h1>
                <Pill tone="primary">v2</Pill>
                <Pill tone={statusTone}>{statusLabel}</Pill>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span className="font-mono text-xs text-gray-500">{session.session_id}</span>
                <button
                  onClick={copySessionId}
                  className="text-gray-400 hover:text-gray-600"
                  title="Copy session ID"
                >
                  <DocumentDuplicateIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="text-right text-xs text-gray-500 space-y-0.5">
              <div>
                Created:{' '}
                <span className="text-gray-800">
                  {new Date(session.created_at).toLocaleString()}
                </span>
              </div>
              {expiresAt && (
                <div>
                  Expires:{' '}
                  <span className={isExpired ? 'text-red-600 font-medium' : 'text-gray-800'}>
                    {expiresAt.toLocaleString()}
                  </span>
                </div>
              )}
              {session.completed_at && (
                <div>
                  Completed:{' '}
                  <span className="text-gray-800">
                    {new Date(session.completed_at).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs font-medium text-gray-600 mb-1">Current step</div>
            <div className="text-xl font-semibold text-gray-900">{currentStepLabel}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs font-medium text-gray-600 mb-1">Progress</div>
            <div className="text-xl font-semibold text-gray-900">
              {stepsCompleted} <span className="text-base text-gray-500">/ 7</span>
            </div>
            <div className="mt-2 h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full"
                style={{ width: `${Math.min(100, (stepsCompleted / 7) * 100)}%` }}
              />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs font-medium text-gray-600 mb-1">Time spent</div>
            <div className="text-xl font-semibold text-gray-900">{timeSpent}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              {session.completed_at
                ? 'created → completed'
                : 'created → now'}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs font-medium text-gray-600 mb-1">Language</div>
            <div className="text-xl font-semibold text-gray-900 font-mono">{language}</div>
          </div>
        </div>

        {/* Site brief */}
        <SiteBriefCard session={session} />

        {/* Marketing attribution */}
        <UtmAttributionCard session={session} />

        {/* Step timeline */}
        {progress && <StepTimelineCard session={session} progress={progress} />}

        {/* Initial prompt analysis */}
        <PromptAnalysisCard session={session} />

        {/* Generation outcome */}
        <GenerationOutcomeCard linkedWebsite={session.linkedWebsite} />

        {/* Raw context */}
        <details className="bg-white rounded-lg shadow p-6">
          <summary className="cursor-pointer list-none">
            <CardHeader
              icon={DocumentDuplicateIcon}
              title="Raw session data (JSON)"
              subtitle="Full row dump for debugging"
            />
          </summary>
          <pre className="mt-4 text-xs text-gray-800 bg-gray-50 rounded-lg p-4 overflow-x-auto max-h-[500px]">
            {JSON.stringify(
              {
                context: session.context,
                answers: session.answers,
                current_question: session.current_question,
                progress: session.progress,
              },
              null,
              2
            )}
          </pre>
        </details>
      </div>
    </AdminLayout>
  );
};

export default WizardSessionDetail;
